/**
 * ตรวจคำตอบที่พิมพ์
 *
 * โจทย์: kana ตัวเดียวสะกด romaji ได้หลายแบบ (し = shi/si)
 * ถ้าไม่รับให้ครบ = ตอบถูกแต่เด้งผิด = พังกำลังใจ ซึ่งแพงกว่าความเข้มงวด
 */
import type { Card } from './types'

/** พิมพ์แล้วยังตัดสินไม่ได้ / ถูก / ผิด */
export type Verdict = 'pending' | 'correct' | 'wrong'

export function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[\s'’ˋ`\-_.]/g, '') // n'a → na · โน้ตแบบ n-a ก็รับ
    .trim()
}

/** คำตอบทั้งหมดที่ยอมรับ (normalize แล้ว) */
export function acceptedAnswers(card: Card): string[] {
  const set = new Set<string>()
  set.add(normalize(card.back))
  for (const a of card.alts ?? []) set.add(normalize(a))
  return [...set]
}

/**
 * ตัดสินสถานะระหว่างพิมพ์ — ทำให้ไม่ต้องกด Enter
 *
 * `pending` = ยังเป็นต้นทางของคำตอบใดคำตอบหนึ่งอยู่ (พิมพ์ "sh" ระหว่างทางไป "shi")
 * `wrong`   = ไม่มีคำตอบไหนขึ้นต้นด้วยสิ่งที่พิมพ์แล้ว → ตัดสินได้ทันที ไม่ต้องรอ
 */
export function judge(input: string, card: Card): Verdict {
  const v = normalize(input)
  if (v.length === 0) return 'pending'

  const answers = acceptedAnswers(card)
  if (answers.includes(v)) {
    // ถ้ายังมีคำตอบที่ยาวกว่าและขึ้นต้นด้วยสิ่งนี้ ให้รอต่อ
    // เช่น ん = "n" แต่ な = "na" — บริบทนี้ตัดสินถูกได้เลยเพราะเทียบทีละการ์ด
    return 'correct'
  }
  return answers.some((a) => a.startsWith(v)) ? 'pending' : 'wrong'
}

/** ยาวสุดที่เป็นไปได้ — ใช้ตั้ง maxlength ของช่องพิมพ์ */
export function maxAnswerLength(card: Card): number {
  return Math.max(...acceptedAnswers(card).map((a) => a.length))
}

/** คำตอบที่ใกล้สิ่งที่พิมพ์ที่สุด — โชว์ตอนเฉลยว่า "ที่ถูกคือ..." */
export function bestAnswer(card: Card): string {
  return normalize(card.back)
}
